import { useState } from "react";
import { api, formatApiError } from "./api";

/**
 * Modal for the signed-in user to change their own password.
 * Open by passing `open=true`; close via `onClose`.
 */
export default function ChangePasswordModal({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCur(""); setNext(""); setConfirm("");
    setErr(""); setDone(false); setBusy(false);
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      setDone(true);
      setTimeout(close, 1600);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} onClick={close} data-testid="admin-changepw-modal">
      <div style={S.panel} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <div>
            <div style={S.title}>Change my password</div>
            <div style={S.sub}>You'll stay signed in on this device.</div>
          </div>
          <button data-testid="admin-changepw-close"
                  onClick={close} style={S.close}>×</button>
        </div>

        {done ? (
          <div style={{ ...S.body, color: "#a7f3d0" }}>
            ✓ Password updated.
          </div>
        ) : (
          <form onSubmit={submit} style={S.body}>
            {err && <div style={S.err} data-testid="admin-changepw-error">{err}</div>}

            <Field label="Current password">
              <input data-testid="admin-changepw-current"
                     type="password" autoFocus required
                     value={cur}
                     onChange={(e) => setCur(e.target.value)}
                     style={S.input} />
            </Field>
            <Field label="New password (min 6 chars)">
              <input data-testid="admin-changepw-new"
                     type="password" required minLength={6}
                     value={next}
                     onChange={(e) => setNext(e.target.value)}
                     style={S.input} />
            </Field>
            <Field label="Confirm new password">
              <input data-testid="admin-changepw-confirm"
                     type="password" required minLength={6}
                     value={confirm}
                     onChange={(e) => setConfirm(e.target.value)}
                     style={S.input} />
            </Field>

            <div style={S.foot}>
              <button type="button" onClick={close} style={S.linkBtn}>Cancel</button>
              <button data-testid="admin-changepw-submit"
                      type="submit" disabled={busy}
                      style={{ ...S.primary, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Saving…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const S = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "10vh 16px", zIndex: 110,
  },
  panel: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 12,
    width: "100%", maxWidth: 420, color: "#e2e8f0",
  },
  head: {
    display: "flex", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: "1px solid #1f2a44",
  },
  title: { fontSize: 16, fontWeight: 700, color: "#f1f5f9" },
  sub:   { fontSize: 12, color: "#64748b", marginTop: 2 },
  close: {
    background: "transparent", border: "none", color: "#94a3b8",
    fontSize: 24, cursor: "pointer", lineHeight: 1, padding: "0 4px",
  },
  body: { padding: 20 },
  input: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1px solid #334155", background: "#0b1220",
    color: "#f1f5f9", fontSize: 13, outline: "none", boxSizing: "border-box",
  },
  err: {
    background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.5)",
    color: "#fecaca", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13,
  },
  foot: {
    display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8,
  },
  primary: {
    background: "#10b981", color: "white", border: "none",
    padding: "10px 18px", borderRadius: 8, fontWeight: 700, cursor: "pointer",
  },
  linkBtn: {
    background: "transparent", border: "1px solid #334155",
    color: "#cbd5e1", padding: "10px 14px", borderRadius: 8,
    cursor: "pointer", fontSize: 13,
  },
};
